<template>
  <div 
    class="global-notification-card"
    :class="{ 
      'read': notification.read, 
      'unread': !notification.read,
      [`priority-${notification.priority}`]: true,
      [`type-${notification.notificationType}`]: true
    }"
    @click="handleClick"
  >
    <div class="global-notification-card__header">
      <div class="global-notification-card__badges">
        <span 
          class="global-notification-card__type-badge"
          :style="{ backgroundColor: notification.getTypeBadgeColor() }"
        >
          {{ notification.getTypeDisplayName() }}
        </span>
        <span 
          class="global-notification-card__priority-indicator"
          :title="`Priority: ${notification.priority}`"
          v-if="notification.priority === 'urgent'"
        >
          {{ notification.getPriorityIcon() }}
        </span>
      </div>
      <div class="global-notification-card__meta">
        <span class="global-notification-card__timestamp">
          {{ notification.createdAt }}
        </span>
        <div 
          class="global-notification-card__read-button"
          @click.stop="toggleReadStatus"
          v-if="!hideReadButton"
        >
          <v-icon v-if="!notification.read" color="n2">
            mdi-checkbox-marked-circle-outline
          </v-icon>
          <v-icon v-else color="y1">
            mdi-check-circle
          </v-icon>
        </div>
      </div>
    </div>
    
    <h4 class="global-notification-card__title">
      {{ notification.title }}
    </h4>
    
    <p class="global-notification-card__content">
      {{ notification.content }}
    </p>
    
    <div class="global-notification-card__footer">
      <span class="global-notification-card__author">
        By {{ notification.createdByName }}
      </span>
      <v-btn
        v-if="notification.linkUrl"
        class="global-notification-card__action-button"
        small
        outlined
        color="y1"
        @click.stop="openLink"
      >
        {{ notification.linkText || 'Learn More' }}
        <v-icon small class="ml-1">mdi-open-in-new</v-icon>
      </v-btn>
    </div>

    <!-- Unread indicator -->
    <div 
      v-if="!notification.read" 
      class="global-notification-card__unread-dot"
    />
  </div>
</template>

<script>
export default {
  name: 'GlobalNotificationCard',
  props: {
    notification: {
      type: Object,
      required: true
    },
    hideReadButton: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleClick() {
      // Mark as read when clicked (unless already read)
      if (!this.notification.read) {
        this.markAsRead()
      }
      
      // If there's a link, open it
      if (this.notification.linkUrl) {
        this.openLink()
      }
    },
    
    toggleReadStatus() {
      if (!this.notification.read) {
        this.markAsRead()
      }
    },
    
    markAsRead() {
      this.$emit('mark-as-read', this.notification.id)
    },
    
    openLink() {
      if (this.notification.linkUrl) {
        window.open(this.notification.linkUrl, '_blank')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.global-notification-card {
  position: relative;
  background: $N5;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 4px solid transparent;

  &:hover {
    background: $N7;
    
    .global-notification-card__read-button {
      opacity: 1;
    }
  }

  &.unread {
    border-left-color: $Y1;
  }

  &.read {
    opacity: 0.8;
  }

  // Priority-specific styling
  &.priority-urgent {
    border-left-color: #dc3545;
  }

  &.priority-high {
    border-left-color: #fd7e14;
  }

  &.priority-medium {
    border-left-color: #ffc107;
  }

  &.priority-low {
    border-left-color: #007bff;
  }

  // Type-specific styling
  &.type-news {
    .global-notification-card__type-badge {
      background-color: #007bff !important;
    }
  }

  &.type-feature_update {
    .global-notification-card__type-badge {
      background-color: #28a745 !important;
    }
  }

  &.type-announcement {
    .global-notification-card__type-badge {
      background-color: #6c757d !important;
    }
    
    &.priority-urgent {
      .global-notification-card__type-badge {
        background-color: #fd7e14 !important;
      }
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__type-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: white;
    letter-spacing: 0.5px;
  }

  &__priority-indicator {
    font-size: 14px;
    display: inline-block;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__timestamp {
    font-size: 12px;
    color: $N2;
  }

  &__read-button {
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: pointer;

    &:hover {
      .v-icon {
        color: $Y1 !important;
      }
    }
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: $white;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }

  &__content {
    font-size: 13px;
    color: $N2;
    margin: 0 0 12px 0;
    line-height: 1.5;
    
    // Truncate long content
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__author {
    font-size: 11px;
    color: $N3;
    font-style: italic;
  }

  &__action-button {
    font-size: 11px !important;
    height: 28px !important;
    padding: 0 12px !important;
    border-radius: 4px !important;
    text-transform: none !important;
    font-weight: 500 !important;
    
    .v-icon {
      font-size: 14px !important;
    }
    
    &:hover {
      background-color: rgba(255, 193, 7, 0.1) !important;
    }
  }

  &__unread-dot {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 8px;
    height: 8px;
    background-color: $Y1;
    border-radius: 50%;
  }
}

// Show read button on hover for unread notifications
.global-notification-card.unread:hover {
  .global-notification-card__read-button {
    opacity: 1;
  }

  .global-notification-card__unread-dot {
    opacity: 0;
  }
}
</style>
